import { Signal, computed, effect, inject } from '@angular/core';
import { RightsService } from '@jms/utils/util.permissions';
import { Command } from '@workbuddy/ui/model';
import { ActivityEntityName, ContactEntity, entityQueueRecipientRights, ContactType, EntityQueue, EntityQueueFieldSource, EntityQueueTableConfig, EntityQueueTableField, EntityQueueType, EntityView, EntityViewDisplay, EntityViewSortDirection, EntityViewVisibility, LabelType, MetadataEntityData, MetadataEntityFieldDomain } from '@jms/model';
import { AdvancedFilterPanelComponent, SettingsPanelBuilder, SettingsPanelSelectOption, SettingsPanelValidationResult, buildDisplayPanel, displayPanelSummary, filterSummary, splitPinnedSelection, toFieldOptions } from '@workbuddy/ui/v2/blocks/settings.panel';
import { FilterFieldProvider } from '@workbuddy/ui/v2/blocks/filter';
import { EntityFilterFieldProvider } from '@workbuddy/ui/v2/blocks/entity.board/providers';
import { ViewSettingsValue } from '@workbuddy/ui/v2/blocks/entity.board/components/view.settings';
import { QueueFieldOption } from '../../queue.field.options';
import { resolveQueueAppearance } from '../../queue.appearance';
import { nameRequired } from '../validators/name.required.validator';
import { injectQueueNameUniqueChecker, nameUnique } from '../validators/name.unique.validator';
import { QueueAdapterDeps, QueueStepAdapter } from './queue.step.adapter';
import { allowedQueueVisibilities } from '../../queue.permissions.policy';
import { QUEUE_SHARED_WITH_TEAM_GROUP, createQueueSharedWithSummary } from '../../queue.shared.with.summary';

const FLAG_QUEUE_SORT_FIELD = 'flag.createdAt';
const FLAG_QUEUE_SORT_OPTIONS: SettingsPanelSelectOption[] = [{ id: FLAG_QUEUE_SORT_FIELD, label: 'Created', group: 'Flags' }];

export interface FlagAdapterConfig {
  entity: ActivityEntityName;
  entityLabel: string;
  resolveColumnOptions: (metadataMap: Record<string, MetadataEntityData>) => QueueFieldOption[];
  defaultTable: () => EntityQueueTableConfig;
}

export function createFlagAdapter(deps: QueueAdapterDeps, config: FlagAdapterConfig): QueueStepAdapter {

  const isQueueNameUnique = injectQueueNameUniqueChecker();
  const rights = inject(RightsService);

  const flagFilterProvider = new EntityFilterFieldProvider();
  const targetEntityFilterProvider = new EntityFilterFieldProvider();

  const $metadataMap = computed(() => deps.$config()?.metadataMap ?? {});
  const $columnOptions = computed(() => config.resolveColumnOptions($metadataMap()));

  // One Columns picker shows, hides and pins; a pinned row is a fixed column, so no column can land in both lists.
  const $tableColumnOptions = computed(() => toFieldOptions($columnOptions()));
  const $sortOptions: Signal<SettingsPanelSelectOption[]> = computed(() => FLAG_QUEUE_SORT_OPTIONS);

  effect(() => {

    const flagMeta = $metadataMap()[ActivityEntityName.Flag];
    if (!flagMeta) return;
    // Flag queues drive their own status logic — hide the status field from the filter editor.
    const domain = new MetadataEntityFieldDomain(flagMeta);
    const fields = Object.values(domain.uiFields).filter((f: any) => f.filterable && (f.path ?? f.name) !== 'status');
    flagFilterProvider.configureFields(ActivityEntityName.Flag, fields);
  });

  effect(() => {

    const metadataMap = $metadataMap();
    const targetMeta = metadataMap[config.entity];
    if (!targetMeta) return;
    const domain = new MetadataEntityFieldDomain(targetMeta);
    const fields = Object.values(domain.uiFields).filter((f: any) => f.filterable);
    targetEntityFilterProvider.configureFields(config.entity, fields);
  });

  const asArray = (value: unknown): unknown[] => Array.isArray(value) ? value : [];

  const toTableFields = (ids: string[], fields: QueueFieldOption[]) =>
    (ids ?? []).map(id => {

      const f = fields.find(f => f.path === id);
      const source = f?.source === 'flag' ? EntityQueueFieldSource.Flag : EntityQueueFieldSource.Entity;
      return { ref: { source, path: id }, name: f?.displayName ?? id, visible: true };
    });

  const buildSharedWith = (value: unknown): ContactEntity[] => {

    if (!Array.isArray(value)) return [];

    return value.map(entry => {

      if (typeof entry === 'string') return { _id: entry } as ContactEntity;
      if (!entry || typeof entry !== 'object') return undefined;

      const contact = entry as Partial<ContactEntity>;
      if (!contact._id) return undefined;
      return { _id: contact._id, name: contact.name, type: contact.type } as ContactEntity;
    }).filter((entry): entry is ContactEntity => !!entry);
  };

  const withoutFlagStatusRules = (filter: any): any => {

    if (!filter?.rules?.length) return filter;

    const rules = filter.rules
      .map((rule: any) => rule?.rules ? withoutFlagStatusRules(rule) : rule)
      .filter((rule: any) => !!rule && (rule.rules?.length || (rule.fieldId !== 'status' && rule.fieldId !== 'flag.status')));

    return rules.length ? { ...filter, rules } : null;
  };

  const resolveInitialTable = (model: Partial<EntityQueue>, isEdit: boolean): EntityQueueTableConfig => {

    if (isEdit) return model.table ?? config.defaultTable();
    return model.table?.fixed?.length || model.table?.other?.length ? model.table : config.defaultTable();
  };

  const buildValue = (formValues: Record<string, unknown>, model: Partial<EntityQueue>): Partial<EntityQueue> => {

    const values = formValues as Record<string, any>;
    const fields = $columnOptions();
    const columns = splitPinnedSelection(values.list?.columns);
    const standardColumns = columns.unpinned as string[];
    const fixedColumns = columns.pinned as string[];

    const queue: Partial<EntityQueue> = {
      name: values.name ?? model.name,
      type: EntityQueueType.Flag,
      visibility: values.visibility,
      sharedWith: values.visibility === EntityViewVisibility.Shared ? buildSharedWith(values.sharedWith) : [],
      targetEntity: config.entity,
      entityFilter: values.targetEntityFilter ?? undefined,
      flagCategoryIds: values.flagCategoryIds ?? [],
      flagFilter: withoutFlagStatusRules(values.flagFilter) ?? undefined,
      appearance: { tone: values.tone, icon: values.icon || undefined },
    };

    queue.table = {
      display: EntityViewDisplay.Table,
      other: toTableFields(standardColumns, fields),
      fixed: toTableFields(fixedColumns, fields),
      sortBy: { source: EntityQueueFieldSource.Flag, path: FLAG_QUEUE_SORT_FIELD },
      sortOrder: values.list?.sortOrder ?? EntityViewSortDirection.Desc,
    };

    return queue;
  };

  const buildPreviewValue = (formValues: Record<string, unknown>, model: Partial<EntityQueue>): ViewSettingsValue => {

    const queue = buildValue(formValues, model);
    const toViewFields = (fields: EntityQueueTableField[] = []) =>
      fields.map(field => ({ _id: field.ref.path, name: field.name ?? field.ref.path, visible: field.visible ?? true, systemHidden: false }));

    return {
      view: {
        display: EntityViewDisplay.Table,
        table: {
          other: toViewFields(queue.table?.other),
          fixed: toViewFields(queue.table?.fixed),
        },
        sort: queue.table?.sortBy
          ? { _id: queue.table.sortBy.path, direction: queue.table.sortOrder ?? EntityViewSortDirection.Desc }
          : null,
      } as Partial<EntityView>,
      pageSize: queue.table?.pageSize ?? 50,
      request: { preview: { queue } },
    };
  };

  const validate = (formValues: Record<string, unknown>): SettingsPanelValidationResult => {

    const errors = [...(nameRequired(formValues) ?? [])];
    if (!asArray(formValues['flagCategoryIds']).length) errors.push({ field: 'flagCategoryIds', required: true });
    return errors;
  };

  return {
    buildValue,
    build: (ctx, model, wizard) => {

      const isEdit = !!model._id;
      const table = resolveInitialTable(model, isEdit);

      const visibilityCommands = allowedQueueVisibilities(isEdit, deps.$config()?.isOwner ?? true, right => rights.hasRights(right)).map(visibility => new Command({ _id: visibility, label: visibility }));

      const selectedVisibility = visibilityCommands.some(c => c._id === model.visibility)
        ? model.visibility
        : visibilityCommands[0]?._id;

      const defaultAppearance = resolveQueueAppearance({ ...model, type: EntityQueueType.Flag });

      const sharedWithSummary = createQueueSharedWithSummary(model.sharedWith ?? []);

      const builder = new SettingsPanelBuilder(isEdit ? 'Edit Queue' : 'Create Queue')
        .input('queueType', 'Queue Type', { value: 'Flag', disabled: () => true })
        .input('entity', 'Entity', { value: config.entityLabel, disabled: () => true })
        .divider()
        .input('name', 'Name', {
          placeholder: 'Queue name',
          value: model.name ?? '',
          autofocus: isEdit ? 'selectAll' : false,
          errorMessage: { required: 'Queue name is required', unique: 'Queue name already taken' },
        })
        .menu('appearance', 'Appearance', {
          displayValue: ({ form }) => computed(() => {
            const v = form() as Record<string, any>;
            return { icon: v.icon || defaultAppearance.icon, tone: v.tone || defaultAppearance.tone };
          }),
        }, m => {
          m.iconPicker('icon', 'Icon', { value: defaultAppearance.icon });
          m.tonePicker('tone', 'Color', { value: defaultAppearance.tone });
        })
        .tabs('visibility', 'View Access', {
          commands: visibilityCommands,
          selected: selectedVisibility
        })
        .entityPicker('sharedWith', 'Shared With', {
          entity: [ActivityEntityName.ContactEmployee, ActivityEntityName.Team],
          // Only people who can use this queue are offered; the team picker ignores it (teams are mixed)
          entityOptions: { requiredRights: entityQueueRecipientRights(config.entity) },
          mode: 'multi',
          selected: model.sharedWith?.map(e => e._id) ?? [],
          selectedOptions: (model.sharedWith ?? []).map(entry => ({
            id: entry._id,
            label: entry.name ?? entry._id,
            avatar: entry.name,
            group: entry.type === ContactType.Team ? QUEUE_SHARED_WITH_TEAM_GROUP : 'Employees',
            data: entry,
          })),
          groupLabel: (entity) => entity === ActivityEntityName.Team ? QUEUE_SHARED_WITH_TEAM_GROUP : 'Employees',
          onSelect: option => { sharedWithSummary.remember(option); return undefined; },
          hidden: (values) => values.visibility !== EntityViewVisibility.Shared,
          errorMessage: { required: 'Select at least one member' },
          validator: ({ value, form }) => form.visibility === EntityViewVisibility.Shared && !asArray(value).length ? { required: true } : null,
          displayValue: ({ field }) => computed(() => sharedWithSummary.describe(field())),
        })
        .divider()
        .entityPicker('flagCategoryIds', 'Flags', {
          entity: ActivityEntityName.Tag,
          mode: 'multi',
          selected: model.flagCategoryIds ?? [],
          entityOptions: { type: LabelType.FlagCategory },
          displayValue: ({ field }) => computed(() => { const v = field() as unknown[]; return v?.length ? `${v.length} flag${v.length === 1 ? '' : 's'} selected` : 'Select flags'; }),
          errorMessage: { required: 'Select at least one flag' },
        })
        .navigate('flagFilter', 'Flag Filter', {
          component: AdvancedFilterPanelComponent,
          inputs: { fieldKey: 'flagFilter', autoApply: true },
          initialValue: model.flagFilter ?? {},
          displayValue: filterSummary(),
          navigateOptions: { providers: [{ provide: FilterFieldProvider, useValue: flagFilterProvider }] },
        })
        .navigate('targetEntityFilter', `${config.entityLabel} Filter`, {
          component: AdvancedFilterPanelComponent,
          inputs: { fieldKey: 'targetEntityFilter', autoApply: true },
          initialValue: model.entityFilter ?? {},
          displayValue: filterSummary(),
          navigateOptions: { providers: [{ provide: FilterFieldProvider, useValue: targetEntityFilterProvider }] },
        })
        .divider()
        .menu('displaySettings', 'Display', { displayValue: displayPanelSummary([EntityViewDisplay.Table]) }, m => {
          buildDisplayPanel(m, {
            types: [EntityViewDisplay.Table],
            selected: EntityViewDisplay.Table,
            sortableOptions: $sortOptions,
            table: {
              columnOptions: $tableColumnOptions,
              selectedColumns: table.other?.map(f => f.ref.path) ?? [],
              selectedFixedColumns: table.fixed?.map(f => f.ref.path) ?? [],
              sortBy: FLAG_QUEUE_SORT_FIELD,
              sortOrder: table.sortOrder ?? EntityViewSortDirection.Desc,
            },
          });
        });

      builder.footerRight(new Command({ label: 'Cancel', action: () => ctx.onCancel() }));

      if (isEdit)
        builder.footerLeft(new Command({ label: 'Delete', action: () => ctx.onDelete() }));

      builder.footerSplitButton(new Command({
        label: isEdit ? 'Save Queue' : 'Create Queue',
        action: () => wizard.next(),
      }), []);

      builder
        .validator(validate)
        .validatorAsync(nameUnique(name => isQueueNameUnique(name, model._id)));

      const viewPaths = new Set([
        'list.columns',
        'list.sortBy',
        'list.sortOrder',
      ]);
      const requestPaths = new Set([
        'flagCategoryIds',
        'flagFilter',
        'targetEntityFilter',
        'list.sortBy',
        'list.sortOrder',
      ]);

      const form = builder.build();
      form.onUpdate((path, value) => {

        const previewValue = buildPreviewValue(form.value() as Record<string, unknown>, model);
        if (viewPaths.has(path)) ctx.onPreviewView(previewValue);
        if (requestPaths.has(path)) ctx.onPreviewRequest(previewValue);
      });
      const initial = buildPreviewValue(form.value() as Record<string, unknown>, model);
      ctx.onPreviewView(initial);
      ctx.onPreviewRequest(initial);

      return form;
    },
  };
}
